import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Close,
  Search,
  Send,
  Description,
} from "@mui/icons-material";
import { getTemplatesApi, sendTemplateApi } from "../../../../api/chat.api";

const TemplatePicker = ({ open, onClose, conversationId, onTemplateSent }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [parameters, setParameters] = useState({});
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [open]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getTemplatesApi();
      const templatesData = res.data.data || res.data || [];

      setTemplates(templatesData?.templates.filter((t) => t.status === "APPROVED"));
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.category?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    // Initialize parameters with empty strings
    const initialParams = {};
    template.body.variables.forEach((v) => {
      initialParams[v.index] = "";
    });
    setParameters(initialParams);
  };

  const handleParameterChange = (index, value) => {
    setParameters((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const getPreviewWithParams = () => {
    if (!selectedTemplate) return "";

    let text = selectedTemplate.body.text;

    // Replace variables with parameter values or placeholders
    selectedTemplate.body.variables.forEach((v) => {
      const value = parameters[v.index] || `{{${v.index}}}`;
      text = text.replace(new RegExp(`\\{\\{${v.index}\\}\\}`, "g"), value);
    });

    return text;
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate) return;

    // Check if all required parameters are filled
    const requiredParams = selectedTemplate.body.variables;
    const missingParams = requiredParams.filter((v) => !parameters[v.index]);

    if (missingParams.length > 0) {
      setError(
        `Please fill in all ${missingParams.length} required parameter(s)`,
      );
      return;
    }

    setSending(true);
    setError("");
    try {
      const paramValues = requiredParams.map((v) => parameters[v.index]);
      await sendTemplateApi(conversationId, {
        templateName: selectedTemplate.name,
        languageCode: selectedTemplate.language,
        parameters: paramValues,
      });

      onTemplateSent?.();
      handleClose();
    } catch (err) {
      console.error("Failed to send template:", err);
      setError(
        err.response?.data?.message ||
          "Failed to send template. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setParameters({});
    setSearch("");
    setError("");
    onClose();
  };

  const getCategoryColor = (category) => {
    const colors = {
      MARKETING: "#4CAF50",
      UTILITY: "#2196F3",
      AUTHENTICATION: "#FF9800",
    };
    return colors[category] || "#757575";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          height: selectedTemplate ? "auto" : "600px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Description color="primary" />
          <Typography variant="h6">WhatsApp Templates</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : !selectedTemplate ? (
          <Box sx={{ p: 2 }}>
            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Template List */}
            <Box sx={{ maxHeight: 450, overflowY: "auto" }}>
              {filteredTemplates.length === 0 ? (
                <Typography
                  color="text.secondary"
                  align="center"
                  sx={{ mt: 4 }}
                >
                  {search ? "No templates found" : "No templates available"}
                </Typography>
              ) : (
                filteredTemplates.map((template) => (
                  <Paper
                    key={template.id}
                    elevation={0}
                    onClick={() => handleSelectTemplate(template)}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.category}
                        size="small"
                        sx={{
                          bgcolor: getCategoryColor(template.category),
                          color: "white",
                          fontSize: 10,
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    {template.header.text && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontWeight: 600,
                          mb: 0.5,
                          color: "text.primary",
                        }}
                      >
                        {template.header.text}
                      </Typography>
                    )}

                    <Typography
                      variant="body2"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: "text.secondary",
                      }}
                    >
                      {template.body.text}
                    </Typography>

                    {template.body.variables.length > 0 && (
                      <Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
                        {template.body.variables.map((v) => (
                          <Chip
                            key={v.index}
                            label={`{{${v.index}}}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 10, height: 20 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            {/* Template Details */}
            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<Close />}
                onClick={() => setSelectedTemplate(null)}
                sx={{ mb: 2 }}
              >
                Back to templates
              </Button>

              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {selectedTemplate.name}
                  <Chip
                    label={selectedTemplate.category}
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: getCategoryColor(selectedTemplate.category),
                      color: "white",
                      fontSize: 10,
                    }}
                  />
                </Typography>

                {selectedTemplate.header.text && (
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ mt: 2, mb: 1 }}
                  >
                    {selectedTemplate.header.text}
                  </Typography>
                )}

                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {getPreviewWithParams()}
                </Typography>

                {selectedTemplate.footer.text && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, color: "text.secondary" }}
                  >
                    {selectedTemplate.footer.text}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Parameters Input */}
            {selectedTemplate.body.variables.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Fill in the variables:
                </Typography>
                <Stack spacing={2}>
                  {selectedTemplate.body.variables.map((variable) => (
                    <TextField
                      key={variable.index}
                      fullWidth
                      label={`Variable ${variable.index}`}
                      placeholder={`Enter value for {{${variable.index}}}`}
                      value={parameters[variable.index] || ""}
                      onChange={(e) =>
                        handleParameterChange(variable.index, e.target.value)
                      }
                      size="small"
                      required
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: 1, ml: 1 }}
        >
          {selectedTemplate
            ? `${selectedTemplate.body.variables.length} variable(s) required`
            : `${filteredTemplates.length} template(s) available`}
        </Typography>
        <Button onClick={handleClose}>Cancel</Button>
        {selectedTemplate && (
          <Button
            variant="contained"
            onClick={handleSendTemplate}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={16} /> : <Send />}
          >
            {sending ? "Sending..." : "Send Template"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplatePicker;
